/**
 * Onboarding wizard.
 *
 * Reuses Stage 1 endpoints (TenantService.updateSelf for region/currency, the
 * media library for the logo, and the invitations feature for invites) so this
 * page is mostly a state machine over /api/onboarding/state.
 *
 * Steps shown in order with check marks, a "Skip" link (which still calls the
 * complete-step endpoint), and a final celebratory toast.
 */
import React, { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Circle } from 'lucide-react'
import { PageHeader } from '@/components/composites/PageHeader'
import { ListSkeleton } from '@/components/composites'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMyTenant } from '@/platform/tenant/useMyTenant'
import { useTenantMutations } from '@/features/admin/api/tenants'
import { useMediaMutations } from '@/features/admin/api/media'
import { PendoEvents } from '@/platform/analytics/pendoEvents'
import { useOnboardingState, useOnboardingMutations } from '../api/hooks'
import type { OnboardingStepId } from '@shared/types/registration'

const STEP_LABELS: Record<OnboardingStepId, string> = {
  logo: 'Upload your logo',
  region: 'Confirm your region',
  'default-currency': 'Set your default currency',
  'invite-team': 'Invite teammates',
  tour: 'Finish the guided tour',
}

export const OnboardingWizardPage = (): JSX.Element => {
  const { data: state, isLoading } = useOnboardingState()
  const { completeStep } = useOnboardingMutations()
  const { data: tenantData } = useMyTenant()
  const { updateSelf } = useTenantMutations()
  const { upload } = useMediaMutations()
  const [error, setError] = useState<string | null>(null)
  // Guards `onboardingCompleted` so it fires once when the wizard's overall
  // completion is observed, not on every re-render.
  const completedFired = useRef(false)

  useEffect(() => {
    if (state?.completedAt && !completedFired.current) {
      completedFired.current = true
      PendoEvents.onboardingCompleted({ stepsCompleted: state.steps.length })
    }
  }, [state?.completedAt, state?.steps.length])

  if (isLoading || !state) {
    return (
      <div className="space-y-6">
        <PageHeader title="Onboarding" description="Let’s get your workspace set up." />
        <Card>
          <CardHeader>
            <CardTitle>Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <ListSkeleton rows={5} leading={false} bordered={false} />
          </CardContent>
        </Card>
      </div>
    )
  }

  const done = state.completedAt !== null
  const isComplete = (id: OnboardingStepId): boolean =>
    state.steps.find((s) => s.stepId === id)?.completedAt !== null

  const mark = async (id: OnboardingStepId): Promise<void> => {
    try {
      setError(null)
      const index = state.steps.findIndex((s) => s.stepId === id)
      await completeStep.mutateAsync(id)
      PendoEvents.onboardingStepCompleted({
        step: id,
        index: index === -1 ? state.steps.length : index,
        totalSteps: state.steps.length,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update step')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Welcome aboard"
        description={
          done
            ? 'You finished onboarding. You can revisit any step anytime from Settings.'
            : `Let’s get your workspace set up. ${state.percentComplete}% complete.`
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {state.steps.map((s) => (
            <div
              key={s.stepId}
              className="flex flex-wrap items-center justify-between gap-2 border rounded px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                {s.completedAt ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className="text-sm truncate">{STEP_LABELS[s.stepId]}</span>
                {s.completedAt && (
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {new Date(s.completedAt).toLocaleDateString()}
                  </Badge>
                )}
              </div>
              {!s.completedAt && (
                <Button size="sm" variant="ghost" onClick={() => mark(s.stepId)} className="shrink-0">
                  Mark done
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Step-specific UI helpers */}
      {!isComplete('logo') && (
        <StepCard title="Upload your logo">
          <p className="text-sm text-muted-foreground">
            Your logo appears on quotes, the admin dashboard, and emails.
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file) return
              try {
                await upload.mutateAsync({
                  file,
                  label: `${tenantData?.tenant.name ?? 'tenant'} logo`,
                  category: 'tenant_logo',
                  tags: ['logo'],
                })
                await mark('logo')
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Upload failed')
              }
            }}
          />
        </StepCard>
      )}

      {!isComplete('region') && tenantData && (
        <StepCard title="Confirm your region">
          <Select
            value={tenantData.tenant.region}
            onValueChange={async (v) => {
              try {
                await updateSelf.mutateAsync({ region: v as 'NA' | 'UK' })
                await mark('region')
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Update failed')
              }
            }}
          >
            <SelectTrigger className="max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NA">North America</SelectItem>
              <SelectItem value="UK">United Kingdom</SelectItem>
            </SelectContent>
          </Select>
        </StepCard>
      )}

      {!isComplete('default-currency') && tenantData && (
        <StepCard title="Set your default currency">
          <div className="flex gap-2 items-center">
            <Input
              defaultValue={tenantData.tenant.defaultCurrency}
              maxLength={3}
              className="max-w-[100px] uppercase"
              id="currency-input"
            />
            <Button
              size="sm"
              onClick={async () => {
                const input = document.getElementById(
                  'currency-input',
                ) as HTMLInputElement | null
                if (!input) return
                const value = input.value.trim().toUpperCase()
                if (!/^[A-Z]{3}$/.test(value)) {
                  setError('Currency must be a 3-letter ISO code')
                  return
                }
                try {
                  await updateSelf.mutateAsync({ defaultCurrency: value })
                  await mark('default-currency')
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Update failed')
                }
              }}
            >
              Save
            </Button>
          </div>
        </StepCard>
      )}

      {!isComplete('invite-team') && (
        <StepCard title="Invite your team">
          <p className="text-sm text-muted-foreground">
            Open the Users section in Admin to send invitations, then come back
            to mark this step done.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" asChild className="w-full sm:w-auto">
              <a href="/admin/users">Open Users admin</a>
            </Button>
            <Button variant="ghost" onClick={() => mark('invite-team')} className="w-full sm:w-auto">
              Skip for now
            </Button>
          </div>
        </StepCard>
      )}

      {!isComplete('tour') && (
        <StepCard title="Take the guided tour">
          <p className="text-sm text-muted-foreground">
            A 60-second tour shows you the main areas of the app.
          </p>
          <Button onClick={() => mark('tour')}>I’ve completed the tour</Button>
        </StepCard>
      )}
    </div>
  )
}

function StepCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  )
}
